import { CommonModule, NgOptimizedImage } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  OnDestroy,
  signal,
  WritableSignal
} from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ProgramType } from '@k12/models';
import { AwardsService } from '@k12/awards';
import { Subject, takeUntil } from 'rxjs';
import { LoadingSpinnerService } from '@k12/app-services';
import { ToastMessageService } from '@k12/app-services';
import { ToastMessageType } from '@k12/models';
import { AcademicYear } from '@k12/models';
import { AwardFundingDetails } from '@k12/models';
import { errorToastValue } from '@k12/app-services';

@Component({
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    NgOptimizedImage
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-awards-amount-overview',
  styleUrls: ['./awards-amount-overview.component.scss'],
  templateUrl: './awards-amount-overview.component.html'
})
export class AwardsAmountOverviewComponent implements OnDestroy {
  private readonly awardService = inject(AwardsService);
  private readonly destroy$ = new Subject<void>();
  private readonly spinnerService = inject(LoadingSpinnerService);
  private readonly toastMessageService = inject(ToastMessageService);
  private previousYearId?: number;
  public readonly ProgramType = ProgramType;
  public year = input.required<AcademicYear | null>();
  public programType = input.required<string>();
  public awardFundingDetails : WritableSignal<AwardFundingDetails | null> = signal(null);

  constructor() {
    effect(() => {
      const year = this.year();

      if (!year) return;

      if (this.previousYearId !== year.id) {
        this.previousYearId = year.id;
        this.getFundingDetails();
      }
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getFundingDetails(): void {
    if (this.year()) {
      this.spinnerService.showSpinner();
      const programTypeId = this.programType() === ProgramType.ESA ? 1 : 2;
      this.awardService
        .getFundingDetails(this.year()!.id, programTypeId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: AwardFundingDetails) => {
            this.awardFundingDetails.set(response);
            this.spinnerService.hideSpinner();
          },
          error: (err: HttpErrorResponse) => {
            this.spinnerService.hideSpinner();
            this.toastMessageService.showToast(ToastMessageType.Error, errorToastValue(err));
          }
        });
    }
  }
}
